<h2 mat-dialog-title class="ic-title" [class.warn]="!readOnly && !preview.CanConfirm">
    <mat-icon aria-hidden="true">{{ readOnly ? 'description' : (preview.CanConfirm ? 'fact_check' : 'block') }}</mat-icon>
    <span>{{ title }}</span>
</h2>

<mat-dialog-content class="ic-body">
    <!-- Package header -->
    <div class="ic-header">
        <div class="ic-file">
            <mat-icon aria-hidden="true">folder_zip</mat-icon>
            <div class="ic-file-text">
                <span class="ic-file-name" [title]="preview.FileName">{{ preview.FileName }}</span>
                <span class="ic-file-meta">
                    {{ size(preview.SizeBytes) }}
                    <ng-container *ngIf="preview.ImportName"> &middot; import <strong>{{ preview.ImportName }}</strong></ng-container>
                    <ng-container *ngIf="uploader"> &middot; uploaded by {{ uploader }}</ng-container>
                </span>
            </div>
        </div>
        <dl class="ic-facts" *ngIf="!preview.Error">
            <div><dt>Organization</dt><dd>{{ preview.Organization || '–' }}</dd></div>
            <div><dt>Environment</dt><dd>{{ preview.Environment || '–' }}</dd></div>
            <div><dt>Config</dt><dd><code>{{ preview.EtlConfigCode }}</code></dd></div>
            <div><dt>Files</dt><dd>{{ preview.Items.length }}<ng-container *ngIf="totalRecords !== null"> &middot; {{ totalRecords | number }} records</ng-container></dd></div>
            <div><dt>IA copy</dt><dd>{{ preview.IsJaiEnabled ? 'On' : 'Off' }}</dd></div>
        </dl>
    </div>

    <!-- History: the decision and the run this upload became -->
    <div class="ic-decision" *ngIf="readOnly && entry">
        <mat-icon aria-hidden="true">{{ entry.Status === 'Confirmed' ? 'check_circle' : entry.Status === 'Discarded' ? 'delete' : entry.Status === 'Rejected' ? 'block' : 'schedule' }}</mat-icon>
        <div class="ic-decision-text">
            <span>{{ decisionText }}</span>
            <span class="ic-decision-run" *ngIf="run && runMeta() as m">
                Import <code>{{ run.RunId }}</code>:
                <span class="df-badge" [ngClass]="'tone-' + m.tone"><mat-icon aria-hidden="true">{{ m.icon }}</mat-icon>{{ m.label }}</span>
                <ng-container *ngIf="run.RecordsSucceeded !== null"> &middot; {{ run.RecordsSucceeded | number }} ok<ng-container *ngIf="run.RecordsFailed"> &middot; {{ run.RecordsFailed | number }} failed</ng-container></ng-container>
            </span>
            <span class="ic-decision-run ic-muted" *ngIf="!run && entry.Status === 'Confirmed'">The worker has not queued this upload yet.</span>
        </div>
    </div>

    <!-- Unreadable package -->
    <div class="ic-error" role="alert" *ngIf="preview.Error">
        <mat-icon aria-hidden="true">error</mat-icon>
        <div>
            <p>{{ preview.Error }}</p>
            <p class="ic-muted">A manifest.json at the root of the zip is required. Fix the package and upload it again.</p>
        </div>
    </div>

    <ng-container *ngIf="!preview.Error">
        <p class="ic-intro">
            Each file's <code>retaineditems</code> decides how the import treats the data already in the system.
            Highlighted rows soft-delete records or replace relationships.
        </p>

        <!-- Per-file table -->
        <div class="ic-table-scroll">
            <table mat-table [dataSource]="preview.Items" [trackBy]="trackByEntity" class="ic-table">
                <ng-container matColumnDef="entity">
                    <th mat-header-cell *matHeaderCellDef>File</th>
                    <td mat-cell *matCellDef="let item">
                        <div class="ic-entity">
                            <span class="ic-entity-name">
                                {{ item.Entity }}
                                <span class="ic-tag" *ngIf="item.IsMaster" matTooltip="Master entity: records missing from the file are soft-deleted unless retained">master</span>
                                <span class="ic-tag ic-tag-error" *ngIf="!item.IsKnownEntity">unknown</span>
                            </span>
                            <span class="ic-entity-file" [title]="item.FileName">{{ item.FileName }}<ng-container *ngIf="item.SizeBytes !== null"> &middot; {{ size(item.SizeBytes) }}</ng-container></span>
                            <span class="ic-entity-retain" *ngIf="item.RetainedItems.length" [title]="'retaineditems: ' + item.RetainedItems.join('; ')">
                                retains {{ item.RetainedItems.join(', ') }}
                            </span>
                        </div>
                    </td>
                </ng-container>

                <ng-container matColumnDef="records">
                    <th mat-header-cell *matHeaderCellDef class="num">Records</th>
                    <td mat-cell *matCellDef="let item" class="num" [class.ic-cell-error]="!item.FileFound || hasError(item)" [title]="exactRecords(item)">
                        {{ records(item) }}
                    </td>
                </ng-container>

                <ng-container matColumnDef="existing">
                    <th mat-header-cell *matHeaderCellDef>Existing records</th>
                    <td mat-cell *matCellDef="let item">
                        <span class="df-badge" *ngIf="existingMeta(item) as m" [ngClass]="'tone-' + m.tone" [matTooltip]="m.detail">
                            <mat-icon aria-hidden="true">{{ m.icon }}</mat-icon>{{ m.label }}
                        </span>
                    </td>
                </ng-container>

                <ng-container matColumnDef="missing">
                    <th mat-header-cell *matHeaderCellDef>Missing records</th>
                    <td mat-cell *matCellDef="let item">
                        <span class="df-badge" *ngIf="missingMeta(item) as m" [ngClass]="'tone-' + m.tone" [matTooltip]="m.detail">
                            <mat-icon aria-hidden="true">{{ m.icon }}</mat-icon>{{ m.label }}
                        </span>
                    </td>
                </ng-container>

                <ng-container matColumnDef="relationships">
                    <th mat-header-cell *matHeaderCellDef>Relationships</th>
                    <td mat-cell *matCellDef="let item">
                        <div class="ic-rels" *ngIf="item.Relationships.length; else noRels">
                            <span class="df-badge ic-rel" *ngFor="let rel of item.Relationships" [ngClass]="'tone-' + relationshipMeta(rel).tone"
                                  [matTooltip]="rel.Entity + ': ' + relationshipMeta(rel).detail">
                                <mat-icon aria-hidden="true">{{ relationshipMeta(rel).icon }}</mat-icon>{{ rel.Entity }}: {{ relationshipMeta(rel).label }}
                            </span>
                        </div>
                        <ng-template #noRels><span class="ic-muted">&ndash;</span></ng-template>
                    </td>
                </ng-container>

                <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: displayedColumns;"
                    [class.ic-row-destructive]="row.IsDestructive"
                    [class.ic-row-error]="!row.FileFound || hasError(row)"></tr>
            </table>
        </div>

        <!-- Warnings -->
        <div class="ic-warnings" *ngIf="warnings.length">
            <h3>
                {{ warnings.length }} note{{ warnings.length === 1 ? '' : 's' }}
                <ng-container *ngIf="errorCount"> &middot; <span class="ic-count-error">{{ errorCount }} error{{ errorCount === 1 ? '' : 's' }}</span></ng-container>
            </h3>
            <ul>
                <li *ngFor="let w of warnings" [ngClass]="'level-' + w.Level.toLowerCase()">
                    <mat-icon aria-hidden="true">{{ levelMeta(w).icon }}</mat-icon>
                    <span><strong *ngIf="w.Entity">{{ w.Entity }}: </strong>{{ w.Message }}</span>
                </li>
            </ul>
        </div>

        <!-- History: the manifest as uploaded -->
        <div class="ic-manifest" *ngIf="readOnly && manifestJson">
            <button mat-button type="button" color="primary" (click)="showManifest = !showManifest" [attr.aria-expanded]="showManifest">
                <mat-icon aria-hidden="true">{{ showManifest ? 'expand_less' : 'expand_more' }}</mat-icon>
                {{ showManifest ? 'Hide' : 'Show' }} manifest.json
            </button>
            <pre *ngIf="showManifest" class="ic-manifest-json">{{ manifestJson }}</pre>
        </div>

        <p class="ic-blocked" role="alert" *ngIf="!readOnly && !preview.CanConfirm">
            The import worker would reject this package. Fix the errors above and upload it again, or discard it.
        </p>

        <mat-checkbox *ngIf="!readOnly && preview.CanConfirm && preview.RequiresAcknowledgement" class="ic-ack" color="warn"
                      [checked]="acknowledged" (change)="acknowledged = $event.checked">
            {{ acknowledgeText }}
        </mat-checkbox>
    </ng-container>
</mat-dialog-content>

<mat-dialog-actions align="end" class="ic-actions" *ngIf="readOnly">
    <button mat-stroked-button type="button" color="primary" *ngIf="canDownload" [disabled]="downloading" (click)="downloadPackage()">
        <mat-icon aria-hidden="true">download</mat-icon>
        Download package
    </button>
    <button mat-flat-button type="button" color="primary" mat-dialog-close cdkFocusInitial>Close</button>
</mat-dialog-actions>
<mat-dialog-actions align="end" class="ic-actions" *ngIf="!readOnly">
    <span class="ic-actions-hint" *ngIf="!preview.Error && preview.CanConfirm">Nothing is imported until you confirm. Closing keeps the file staged.</span>
    <button mat-button type="button" mat-dialog-close cdkFocusInitial *ngIf="!preview.Error">Decide later</button>
    <button mat-stroked-button type="button" color="warn" (click)="discard()">
        <mat-icon aria-hidden="true">delete</mat-icon>
        Discard upload
    </button>
    <button mat-flat-button type="button" color="primary" (click)="confirm()" [disabled]="!canConfirm" *ngIf="!preview.Error">
        <mat-icon aria-hidden="true">play_circle</mat-icon>
        Queue import
    </button>
</mat-dialog-actions>
